import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { pub, setToken, errMsg } from "@/lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", code: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { if (e.response?.status === 409) { setMode("code"); toast.message(errMsg(e)); } else toast.error(errMsg(e)); } finally { setBusy(false); } };
  const done = (data) => { setToken("admin", data.token); toast.success(`Talofa, ${data.user.name || data.user.email}`); nav("/organizer"); };

  const login = (e) => { e.preventDefault(); run(async () => done((await pub.post("/auth/login", { email: f.email, password: f.password })).data)); };
  const sendCode = (e) => { e.preventDefault(); run(async () => { const { data } = await pub.post("/auth/request-code", { email: f.email }); toast.success(data.message); setMode("set"); }); };
  const setPw = (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("The two passwords don't match.");
    run(async () => done((await pub.post("/auth/set-password", { email: f.email, code: f.code, password: f.password })).data));
  };

  return (
    <div className="min-h-screen bg-stone-900 text-amber-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="font-heading text-xl font-bold text-amber-400">← Fanene ʻOhana</Link>
        <div className="bg-white text-stone-900 rounded-3xl p-7 sm:p-9 mt-6 shadow-2xl space-y-5">
          <ShieldCheck className="w-10 h-10 text-[#2B5338]" />
          <h1 className="text-3xl font-bold text-amber-950">Organizer area</h1>
          {mode === "login" && (
            <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
              <input required type="email" data-testid="admin-login-email-input" className="field" placeholder="Email" value={f.email} onChange={set("email")} />
              <input required type="password" data-testid="admin-login-password-input" className="field" placeholder="Password" value={f.password} onChange={set("password")} />
              <button disabled={busy} data-testid="admin-login-submit" className="btn-primary w-full">Sign in</button>
              <button type="button" data-testid="admin-first-time-button" onClick={() => setMode("code")} className="w-full text-center underline font-semibold py-2">First time here or forgot password?</button>
            </form>
          )}
          {mode === "code" && (
            <form onSubmit={sendCode} className="space-y-4" data-testid="admin-request-code-form">
              <p className="text-lg">We'll email you a 6-digit code so you can set your own password.</p>
              <input required type="email" data-testid="admin-code-email-input" className="field" placeholder="Organizer email" value={f.email} onChange={set("email")} />
              <button disabled={busy} data-testid="admin-request-code-submit" className="btn-primary w-full">Email me a code</button>
              <button type="button" onClick={() => setMode("set")} data-testid="admin-have-code-button" className="w-full underline font-semibold py-2">I already have a code</button>
              <button type="button" onClick={() => setMode("login")} data-testid="admin-back-login-button" className="w-full underline py-2">Back to sign in</button>
            </form>
          )}
          {mode === "set" && (
            <form onSubmit={setPw} className="space-y-4" data-testid="admin-set-password-form">
              <input required type="email" data-testid="admin-set-email-input" className="field" placeholder="Organizer email" value={f.email} onChange={set("email")} />
              <input required data-testid="admin-set-code-input" inputMode="numeric" maxLength={6} className="field tracking-[0.4em] text-center !text-2xl" placeholder="Code" value={f.code} onChange={set("code")} />
              <input required minLength={8} type="password" data-testid="admin-set-password-input" className="field" placeholder="New password (8+ characters)" value={f.password} onChange={set("password")} />
              <input required minLength={8} type="password" data-testid="admin-set-confirm-input" className="field" placeholder="Type it again" value={f.confirm} onChange={set("confirm")} />
              <button disabled={busy} data-testid="admin-set-password-submit" className="btn-primary w-full">Save password & sign in</button>
              <button type="button" onClick={() => setMode("login")} className="w-full underline py-2">Back to sign in</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
